import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Progress, Tooltip } from 'antd';
import { useAuth } from '../../../../providers/AuthProvider';
import DashboardCard, { LastUpdatedPill } from './DashboardCard';
import { UserImageCircleNEW } from '../../../../components/UserImageCircle';

// const getInitials = (name = '') => String(name)
//     .split(' ')
//     .filter(Boolean)
//     .slice(0, 2)
//     .map(part => part[0])
//     .join('')
//     .toUpperCase();

// const ProfileAvatar = ({ auth }) => {
//     const [imageFailed, setImageFailed] = useState(false);

//     const hasImage = !!auth.image_path
//         && !String(auth.image_path).includes('default-user')
//         && !imageFailed;
//     const isConfirmed = !!auth.hasConfirmedAvailabilityForOpportunities?.();

//     return (
//         <Tooltip
//             placement="top"
//             color="#000"
//             title={isConfirmed
//                 ? `${auth.first_name} recently confirmed availability for job assignments`
//                 : `${auth.first_name} is unavailable`}
//         >
//             <div className="relative h-20 w-20 shrink-0 sm:h-[84px] sm:w-[84px]">
//                 {hasImage ? (
//                     <img
//                         src={auth.image_path}
//                         alt={`Profile image for ${auth.fullName()}`}
//                         onError={() => setImageFailed(true)}
//                         className="h-full w-full rounded-full object-cover"
//                     />
//                 ) : (
//                     <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-[#D4C6F7] to-[#A68DE3] text-2xl font-bold text-white">
//                         {getInitials(auth.fullName())}
//                     </div>
//                 )}

//                 <span
//                     className={TB.mapClass('absolute bottom-1 right-1 h-4 w-4 rounded-full border-[3px] border-white', {
//                         'bg-green-500': isConfirmed,
//                         'bg-amber-500': !isConfirmed,
//                     })}
//                 />
//             </div>
//         </Tooltip>
//     );
// };

const LiveBadge = ({ isLive }) => (
    <span
        className={TB.mapClass('inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider', {
            'bg-green-50 text-green-700': isLive,
            'bg-gray-100 text-gray-500': !isLive,
        })}
    >
        <span className={TB.mapClass('h-1.5 w-1.5 rounded-full', { 'bg-green-500': isLive, 'bg-gray-400': !isLive })} />
        {isLive ? 'Live' : 'Paused'}
    </span>
);

const AvailabilityStatus = ({ auth, onReset }) => {
    const isConfirmed = auth.hasConfirmedAvailabilityForOpportunities?.();
    const resetButton = (
        <button
            type="button"
            onClick={onReset}
            className="ml-1 border-0 bg-transparent p-0 text-xs font-medium text-primary underline hover:text-primary-dark"
        >
            Reset
        </button>
    );

    if (isConfirmed) {
        return (
            <p className="m-0 mt-1 text-xs leading-snug text-green-700">
                <i className="fa fa-check mr-1" aria-hidden="true" />
                Availability set through {auth.confirmedAvailabilityExpiration()?.format?.('ll')}
                {resetButton}
            </p>
        );
    }

    return (
        <p className="m-0 mt-1 text-xs font-semibold leading-snug text-danger">
            <span className="mr-1" aria-hidden="true">!</span>
            Availability expired
            {resetButton}
        </p>
    );
};

const SectionStatusIcon = ({ isComplete }) => (
    <span
        className={TB.mapClass('mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white', {
            'bg-[#7DDB9E]': isComplete,
            'bg-amber-500': !isComplete,
        })}
        aria-hidden="true"
    >
        {isComplete
            ? <i className="fas fa-check text-[11px]" />
            : <span className="text-sm font-bold leading-none">!</span>}
    </span>
);

const ProfileSectionLink = ({ section }) => (
    <Link
        to={section.path}
        title={section.title}
        className="group flex items-start gap-3 text-gray-900 hover:text-gray-900 hover:no-underline"
    >
        <SectionStatusIcon isComplete={section.isComplete} />
        <span className="flex min-w-0 flex-col leading-tight">
            <span className="pt-0.5 text-xs group-hover:text-primary">{section.label}</span>
            {!section.isComplete && (
                <span className="text-xs font-semibold text-amber-600">Needs an update</span>
            )}
        </span>
    </Link>
);

// Group the talent's full-profile steps by StepSection — same logic as ProfilePanel.
const getProfileSections = (auth) => {
    const stepSections = TB.groupBy(auth.getFullProfileStepsForTalent(), step => step.StepSection);

    return TB.keys(stepSections).map(label => {
        const steps = stepSections[label];
        const firstStep = TB.first(steps);

        return {
            label,
            path: firstStep.getPath(),
            title: firstStep.StepTitle,
            isComplete: !TB.filter(steps, step => step.NeedsCompletion?.(auth) !== false && !step.hasCompleted(auth))?.length,
        };
    });
};

const ProfileCard = ({
    title = 'Profile',
    totalUpdates,
    onResetAvailability,
    className,
    disabled,
    disabledReason,
    inactive,
    inactiveMessage,
}) => {
    const { auth } = useAuth();

    if (!auth) return null;

    const progress = auth.profileProgress() || 0;
    const sections = getProfileSections(auth);
    const sectionColumns = TB.chunk(sections, TB.ceil(sections.length / 2) || 1);

    return (
        <DashboardCard
            title={title}
            count={totalUpdates}
            className={className}
            disabled={disabled}
            disabledReason={disabledReason}
            inactive={inactive}
            inactiveMessage={inactiveMessage}
            headerRight={<LastUpdatedPill days={auth.lastUpdatedInfo()} />}
            footer={(
                <>
                    <Link
                        to={`/talent-profiles/${auth.id}`}
                        className="inline-flex items-center gap-2 text-xs font-semibold text-primary hover:text-primary-dark hover:no-underline"
                    >
                        <i className="fas fa-eye text-sm" aria-hidden="true" />
                        Client view
                    </Link>

                    <Link
                        to="/talent/application"
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-primary px-5 py-2.5 text-xs text-white hover:bg-primary-dark hover:text-white hover:no-underline"
                    >
                        Edit profile
                    </Link>
                </>
            )}
        >
            {/* Identity */}
            <div className="flex items-center gap-4">
                <UserImageCircleNEW
                    user={auth}
                    className="!h-20 !w-20"
                    // grayscale={false}
                />

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="m-0 truncate text-sm font-semibold leading-tight text-gray-900">
                            {auth.fullName()}
                        </h3>
                        {/* <LiveBadge isLive={!!auth.is_available} /> */}
                    </div>

                    {auth.job_title && (
                        <p className="m-0 mt-0.5 text-xs leading-snug text-gray-600">{auth.job_title}</p>
                    )}

                    {onResetAvailability && (
                        <AvailabilityStatus auth={auth} onReset={() => onResetAvailability()} />
                    )}
                </div>
            </div>

            {/* Profile strength */}
            <div className="mt-7">
                <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600">Profile strength</span>
                    <span className="text-base font-semibold text-gray-900">{progress}%</span>
                </div>
                <Progress
                    percent={progress}
                    showInfo={false}
                    strokeColor="#7DDB9E"
                    trailColor="#EEF0F4"
                    strokeWidth={8}
                    className="m-0"
                />
            </div>

            {/* Profile sections */}
            <div className="mt-5 grid grid-cols-1 gap-x-4 gap-y-4 xs:grid-cols-2">
                {sectionColumns.map((column, index) => (
                    <div key={`profile-sections-${index}`} className="flex flex-col gap-4">
                        {column.map(section => (
                            <ProfileSectionLink key={section.path} section={section} />
                        ))}
                    </div>
                ))}
            </div>
        </DashboardCard>
    );
};

ProfileCard.propTypes = {
    title: PropTypes.node,
    totalUpdates: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onResetAvailability: PropTypes.func,
    className: PropTypes.string,
    disabled: PropTypes.any,
    disabledReason: PropTypes.node,
    inactive: PropTypes.bool,
    inactiveMessage: PropTypes.node,
};

export default ProfileCard;
